// Reading a failed request (ora-fe ADR-0002). ora-be answers an error with a
// JSON body: `{ success: false, error }`, `{ error, code }`, or Fastify's own
// `{ error, message }`. Use these instead of unwrapping `error.response.data`
// by hand. Pure: no store, no axios instance.
import { isAxiosError } from 'axios';

interface ApiErrorBody {
  error?: unknown;
  message?: unknown;
  code?: unknown;
}

const errorBody = (error: unknown): ApiErrorBody | undefined => {
  if (typeof error !== 'object' || error === null) return undefined;

  const data = (error as { response?: { data?: unknown } }).response?.data;

  return typeof data === 'object' && data !== null
    ? (data as ApiErrorBody)
    : undefined;
};

const text = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value : undefined;

/**
 * The server's own message for a failed request: the body's `error`, else its
 * `message`. Undefined when the value carries no response body with one.
 */
export const readApiErrorMessage = (error: unknown): string | undefined => {
  const body = errorBody(error);

  return text(body?.error) ?? text(body?.message);
};

/** The machine-readable `code` of a failed request's body, if it has one. */
export const readApiErrorCode = (error: unknown): string | undefined =>
  text(errorBody(error)?.code);

/**
 * A user-facing message for any thrown value: the server's message, else the
 * message of a plain `Error` or a thrown string, else `fallback`. Axios's own
 * message ("Request failed with status code 500") is never shown.
 */
export const getApiErrorMessage = (
  error: unknown,
  fallback: string
): string => {
  const serverMessage = readApiErrorMessage(error);
  if (serverMessage) return serverMessage;
  if (isAxiosError(error)) return fallback;
  if (error instanceof Error) return text(error.message) ?? fallback;

  return text(error) ?? fallback;
};

/** True when `error` is an AxiosError whose response has one of `statuses`. */
export const isAxiosStatus = (error: unknown, ...statuses: number[]): boolean =>
  isAxiosError(error) &&
  error.response !== undefined &&
  statuses.includes(error.response.status);
